import { Fragment } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SHORTCUT_GROUPS, type ShortcutItem } from "./shortcutCatalog";

/** Renders one shortcut's alternatives: <kbd>g</kbd> then <kbd>i</kbd> / <kbd>↓</kbd>. */
export function ShortcutKeys({ item }: { item: ShortcutItem }) {
    return (
        <span className="flex flex-wrap items-center justify-end gap-1 text-xs text-muted-foreground">
            {item.alternatives.map((seq, i) => (
                <Fragment key={i}>
                    {i > 0 ? <span aria-hidden>/</span> : null}
                    {seq.map((k, j) => (
                        <Fragment key={j}>
                            {j > 0 ? <span>then</span> : null}
                            <kbd className="min-w-[1.5rem] rounded border bg-muted px-1.5 py-0.5 text-center font-mono text-[11px] text-foreground">
                                {k}
                            </kbd>
                        </Fragment>
                    ))}
                </Fragment>
            ))}
        </span>
    );
}

/** The `?` cheat sheet. Radix Dialog → focus trap, Esc to close, aria-modal. */
export function ShortcutsDialog({
    open,
    onOpenChange,
    enabled
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    enabled: boolean;
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Keyboard shortcuts</DialogTitle>
                    <DialogDescription>
                        {enabled
                            ? "Shortcuts pause while you're typing in a text field or editor."
                            : "Shortcuts are turned off."}{" "}
                        <Link
                            to="/settings/shortcuts"
                            onClick={() => onOpenChange(false)}
                            className="font-medium text-primary underline-offset-2 hover:underline"
                        >
                            {enabled ? "Turn off" : "Turn on"} in Settings
                        </Link>
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                    {SHORTCUT_GROUPS.map((group) => (
                        <section key={group.section}>
                            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                {group.section}
                            </h3>
                            <dl className="divide-y text-sm">
                                {group.items.map((item) => (
                                    <div key={item.description} className="flex items-center justify-between gap-3 py-1.5">
                                        <dt className="min-w-0">{item.description}</dt>
                                        <dd className="shrink-0"><ShortcutKeys item={item} /></dd>
                                    </div>
                                ))}
                            </dl>
                        </section>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}
